import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminPatrolSupport } from "../../../../../http/patrol/types/patrolSupports";
import { usePatrolSupportsApi } from "../../../../../http/patrol/usePatrolSupports";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateSupportModalDialogButton from "./CreateSupportModalDialogButton";
import SupportForm from "./SupportForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Supports: FC<Props> = ({ active }) => {
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "supports" });
  const aborter = useRef(new AbortController());
  const patrolSupportsApi = usePatrolSupportsApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await patrolSupportsApi.updateAdminPatrolSupportVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<AdminPatrolSupport>();
  const [itemToDelete, setItemToDelete] = useState<AdminPatrolSupport>();
  const { accessesAction, accessesDialog } = useEntityAccesses<{ id: string; name?: string }>("patrol.support");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "supports");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = patrolSupportsApi.useAdminPatrolSupports(query, aborter.current.signal, patrolSystemId);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.patrolSupports.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.patrolSupports.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateSupportModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminPatrolSupport>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.materials.singular}</th>
            {!patrolSystemId && <th align="left">{i18n.translation.systems.singular}</th>}
            <th align="right">{i18n.translation.common.maxSeamsSpan}</th>
            <th align="right">{i18n.translation.common.maxInsulationThickness}</th>
            <th align="right">{i18n.translation.common.hipsSpans}</th>
            <th align="left">{i18n.translation.common.flatRoofCompatible}</th>
            <th align="left">{i18n.translation.common.type}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.item.code}</td>
            <td>{item.supportMaterial?.name}</td>
            {!patrolSystemId && <td>{item.system?.name}</td>}
            <Table.Td align="right" noWrap>
              {item.maxSeamsSpan ? `${item.maxSeamsSpan} mm` : undefined}
            </Table.Td>
            <Table.Td align="right" noWrap>
              {item.maxInsulationThickness ? `${item.maxInsulationThickness} mm` : undefined}
            </Table.Td>
            <Table.Td align="right" noWrap>
              {item.hipsSpans?.length ? `${item.hipsSpans} mm` : undefined}
            </Table.Td>
            <td>{formatBoolean(i18n.translation, item.flatRoofCompatible)}</td>
            <td>
              {item.forTerminalsAndCurves && item.forIntermediates
                ? i18n.translation.common.terminalCurveAndIntermediate
                : item.forTerminalsAndCurves
                  ? i18n.translation.common.terminalAndCurve
                  : item.forIntermediates
                    ? i18n.translation.common.intermediate
                    : ""}
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction({ id: item.item.id, name: item.item.name ?? item.item.code }),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.patrolSupports.edit}>
          <FlexContent direction="row">
            <SupportForm support={itemToEdit} onClose={() => setItemToEdit(undefined)} />
          </FlexContent>
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.patrolSupports.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                patrolSupportsApi
                  .deleteAdminPatrolSupport(itemToDelete.item.id, patrolSystemId)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.patrolSupports.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Supports;
